/**
 * reportSheetExport — Excel-i dhe PDF-ja e një faqeje raporti, nga i njëjti përshkrim.
 *
 * Faqet e raporteve e kishin secila eksportin e vet: `aoa_to_sheet` me kokën e
 * kolonave dhe asgjë tjetër, ose një `autoTable` blu pa kokë kompanie. Skedari
 * që dilte prej tyre nuk ishte dokument — ishte një dump i tabelës, pa e ditur
 * kush e lëshoi, për cilën periudhë, dhe pa një rresht totali.
 *
 * Ky modul e mban vetëm faqosjen, të përshkruar një herë (`layout`) dhe të
 * lexuar nga të tria daljet — Excel, PDF, printim. Gjuha vjen nga thirrësi:
 * etiketat kalohen të përkthyera, ndaj i njëjti raport del shqip, anglisht ose
 * gjermanisht pa asnjë varg të ngurtë këtu.
 *
 * Paleta dhe hierarkia janë ato të «Listës së Pagave» (`payrollRegisterExport`):
 * titull navy, vijë teal që ndan kokën, kokë e çelët, rreshta zebra me vija të
 * holla, TOTALI me vijë teal sipër, kuti përmbledhëse dhe vend për nënshkrim.
 * Një gjuhë vizuale për çdo dokument që del nga sistemi.
 *
 * ⚠️ `xlsx-js-style`, jo `xlsx`: i dyti i shpërfill stilet në heshtje.
 */
import * as XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { drawCompanyLogo } from './payrollRegisterExport';

const HEX   = { ink: '111827', muted: '6B7280', rule: 'E2E8F0', brand: '4893A5', navy: '303D50', soft: 'F8FAFC', zebra: 'FCFCFD' };
const INK   = [17, 24, 39];
const MUTED = [107, 114, 128];
const RULE  = [226, 232, 240];
const BRAND = [72, 147, 165];
const NAVY  = [48, 61, 80];

/** Shifra si `1.234,56` — i njëjti format në ekran, në Excel dhe në PDF. */
export const money = (v) =>
  Number(v || 0).toLocaleString('sq-AL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const text = (v) => (v === null || v === undefined ? '' : String(v));

/**
 * Përshkrimi i faqosjes me parazgjedhje të plotësuara.
 *
 * Thirrësi jep vetëm atë që ka: një raport pa nënshkrim thjesht nuk e kalon
 * `signature`, dhe blloku nuk vizatohet. Pa këtë, çdo raport do të detyrohej
 * të njihte të gjitha fushat e motrave të veta.
 */
export function sheetLayout(spec) {
  return {
    title: '',
    logo: null,
    left: [],
    right: [],
    note: '',
    columns: [],
    groups: null,
    body: [],
    totals: null,
    totalLabel: 'TOTAL',
    summary: null,
    summaryTitle: '',
    signature: null,
    footer: '',
    sheetName: 'Report',
    fileBase: 'report',
    orientation: 'landscape',
    ...spec,
  };
}

const cellOf = (row, col) => {
  const v = row[col.key];
  if (col.money) return Number(v) || 0;
  return v ?? '';
};

// ── Excel ───────────────────────────────────────────────────────────────────

export function buildSheetWorkbook(input) {
  const L = sheetLayout(input);
  const cols = L.columns;
  const N = cols.length;
  const merges = [];
  const M = (r, c1, c2) => { if (c2 > c1) merges.push({ s: { r, c: c1 }, e: { r, c: c2 } }); };
  const blank = () => Array(N).fill('');
  const aoa = [];
  // Blloku i djathtë nis aty ku i mbetet vend: kolona A është 5 shkronja e gjerë
  // (mban "Nr."), ndaj koka shkruhet në qeliza të bashkuara, jo në A.
  const RIGHT_FROM = Math.max(1, N - 4);
  const LEFT_TO    = Math.max(0, RIGHT_FROM - 2);

  const title = blank(); title[0] = L.title; aoa.push(title);
  M(0, 0, N - 1);

  const headLines = Math.max(L.left.length, L.right.length, 4);
  for (let i = 0; i < headLines; i++) {
    const row = blank();
    row[0] = L.left[i] || '';
    row[RIGHT_FROM] = L.right[i] || '';
    aoa.push(row);
    M(aoa.length - 1, 0, LEFT_TO);
    M(aoa.length - 1, RIGHT_FROM, N - 1);
  }
  const HEAD_BLOCK = headLines;                 // rreshtat 1..headLines

  let NOTE_ROW = -1;
  if (L.note) {
    const nr = blank(); nr[0] = L.note; aoa.push(nr);
    NOTE_ROW = aoa.length - 1;
    M(NOTE_ROW, 0, N - 1);
  }

  aoa.push(blank());
  const RULE_ROW = aoa.length - 1;

  let GROUP_ROW = -1;
  if (L.groups) {
    const g = blank();
    let c = 0;
    L.groups.forEach((grp) => {
      if (grp.label) { g[c] = grp.label; M(aoa.length, c, c + grp.keys.length - 1); }
      c += grp.keys.length;
    });
    aoa.push(g);
    GROUP_ROW = aoa.length - 1;
  }

  aoa.push(cols.map((c) => c.label));
  const HEAD_ROW = aoa.length - 1;

  L.body.forEach((b) => aoa.push(cols.map((c) => cellOf(b, c))));
  const LAST_BODY = aoa.length - 1;

  let TOTAL_ROW = -1;
  if (L.totals) {
    const tr = blank();
    tr[Math.min(1, N - 1)] = L.totalLabel;
    // Një total jo-numerik NUK bëhet zero.
    //
    // `Number('33:04') || 0` jepte `0`, dhe një total i rremë zero është më keq
    // se asnjë total: lexohet si matje. Kohët dalin si `H:MM` (Orët), shumat si
    // numër (Lista e Pagave) — të dyja duhet të mbijetojnë.
    cols.forEach((c, i) => {
      const v = L.totals[c.key];
      if (v === undefined) return;
      const n = Number(v);
      tr[i] = (v !== '' && v !== null && Number.isFinite(n)) ? n : v;
    });
    aoa.push(tr);
    TOTAL_ROW = aoa.length - 1;
  }

  let S_TITLE = -1, S_LABELS = -1, S_VALUES = -1;
  if (L.summary && L.summary.length) {
    aoa.push(blank());
    const st = blank(); st[0] = L.summaryTitle; aoa.push(st);
    S_TITLE = aoa.length - 1;
    M(S_TITLE, 0, 3);

    const labels = blank(); const values = blank();
    L.summary.forEach((s, i) => {
      const c = i * 2;
      if (c >= N) return;                       // më shumë kuti se kolona — s'ka ku
      labels[c] = s.label;
      values[c] = s.money ? Number(s.value) || 0 : s.value;
    });
    aoa.push(labels); S_LABELS = aoa.length - 1;
    aoa.push(values); S_VALUES = aoa.length - 1;
    L.summary.forEach((_, i) => { if (i * 2 + 1 < N) { M(S_LABELS, i * 2, i * 2 + 1); M(S_VALUES, i * 2, i * 2 + 1); } });
  }

  let SIGN_ROW = -1;
  if (L.signature) {
    aoa.push(blank());
    const sg = blank();
    sg[0] = L.signature.left || '';
    sg[RIGHT_FROM] = L.signature.right || '';
    aoa.push(sg);
    SIGN_ROW = aoa.length - 1;
    M(SIGN_ROW, 0, Math.min(3, N - 1));
    M(SIGN_ROW, RIGHT_FROM, N - 1);
  }

  aoa.push(blank());
  const ft = blank(); ft[0] = L.footer; aoa.push(ft);
  const FOOT_ROW = aoa.length - 1;
  M(FOOT_ROW, 0, N - 1);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = cols.map((c) => ({ wch: c.w || 14 }));
  ws['!merges'] = merges;
  ws['!rows'] = [{ hpt: 22 }];

  // ── Stilet ────────────────────────────────────────────────────────────────
  const at = (r, c) => ws[XLSX.utils.encode_cell({ r, c })];
  const style = (r, c, st) => { const cell = at(r, c); if (cell) cell.s = { ...(cell.s || {}), ...st }; };
  const styleRow = (r, st) => { if (r >= 0) for (let c = 0; c < N; c++) style(r, c, st); };
  const line = (color, side = 'bottom', w = 'thin') => ({ [side]: { style: w, color: { rgb: color } } });

  styleRow(0, { font: { bold: true, sz: 14, color: { rgb: HEX.navy } }, alignment: { vertical: 'center' } });
  for (let r = 1; r <= HEAD_BLOCK; r++) {
    style(r, 0, { font: { sz: 9, bold: r === 1, color: { rgb: r === 1 ? HEX.ink : HEX.muted } } });
    style(r, RIGHT_FROM, { font: { sz: 9, color: { rgb: HEX.muted } }, alignment: { horizontal: 'right' } });
  }
  if (NOTE_ROW >= 0) style(NOTE_ROW, 0, { font: { sz: 8, italic: true, color: { rgb: HEX.muted } } });
  styleRow(RULE_ROW, { border: line(HEX.brand, 'bottom', 'medium') });

  if (GROUP_ROW >= 0) {
    styleRow(GROUP_ROW, {
      font: { bold: true, sz: 8, color: { rgb: HEX.muted } },
      alignment: { horizontal: 'center' },
    });
  }
  styleRow(HEAD_ROW, {
    font: { bold: true, sz: 9, color: { rgb: HEX.ink } },
    fill: { fgColor: { rgb: HEX.soft } },
    border: line(HEX.rule),
    alignment: { horizontal: 'center', wrapText: true },
  });

  for (let r = HEAD_ROW + 1; r <= LAST_BODY; r++) {
    const zebra = (r - HEAD_ROW - 1) % 2 === 1;
    cols.forEach((col, c) => {
      style(r, c, {
        font: { sz: 9, bold: !!col.bold, color: { rgb: HEX.ink } },
        alignment: {
          horizontal: col.align || (col.money ? 'right' : 'left'),
          wrapText: !!col.wrap,
          vertical: 'top',
        },
        border: line('F1F5F9'),
        ...(zebra ? { fill: { fgColor: { rgb: HEX.zebra } } } : {}),
      });
    });
  }

  if (TOTAL_ROW >= 0) {
    styleRow(TOTAL_ROW, {
      font: { bold: true, sz: 9.5, color: { rgb: HEX.navy } },
      border: line(HEX.brand, 'top', 'medium'),
    });
    cols.forEach((col, c) => { if (col.money) style(TOTAL_ROW, c, { alignment: { horizontal: 'right' } }); });
  }

  if (S_TITLE >= 0) {
    style(S_TITLE, 0, { font: { bold: true, sz: 9, color: { rgb: HEX.muted } } });
    L.summary.forEach((_, i) => {
      const c = i * 2;
      if (c >= N) return;
      const last = i === L.summary.length - 1;
      style(S_LABELS, c, {
        font: { bold: true, sz: 8, color: { rgb: last ? 'FFFFFF' : HEX.muted } },
        fill: { fgColor: { rgb: last ? HEX.navy : HEX.soft } },
        alignment: { horizontal: 'center' },
        border: line(HEX.rule, 'top'),
      });
      style(S_VALUES, c, {
        font: { bold: true, sz: 12, color: { rgb: last ? 'FFFFFF' : HEX.ink } },
        fill: { fgColor: { rgb: last ? HEX.navy : HEX.soft } },
        alignment: { horizontal: 'center' },
        border: line(HEX.rule),
      });
    });
  }

  if (SIGN_ROW >= 0) {
    style(SIGN_ROW, 0, { font: { sz: 9, color: { rgb: HEX.muted } }, border: line(HEX.rule, 'top') });
    style(SIGN_ROW, RIGHT_FROM, { font: { sz: 9, color: { rgb: HEX.muted } }, alignment: { horizontal: 'right' }, border: line(HEX.rule, 'top') });
  }
  style(FOOT_ROW, 0, { font: { sz: 8, italic: true, color: { rgb: HEX.muted } } });

  // Formati i numrave: pa të, `1234.5` mbetet `1234.5` dhe kolona nuk rreshtohet.
  const MONEY = '#,##0.00';
  const moneyCols = cols.map((c, i) => (c.money ? i : -1)).filter((i) => i >= 0);
  const lastMoneyRow = TOTAL_ROW >= 0 ? TOTAL_ROW : LAST_BODY;
  for (let r = HEAD_ROW + 1; r <= lastMoneyRow; r++) {
    moneyCols.forEach((c) => {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (ws[ref] && typeof ws[ref].v === 'number') ws[ref].z = MONEY;
    });
  }
  if (S_VALUES >= 0) {
    L.summary.forEach((s, i) => {
      const ref = XLSX.utils.encode_cell({ r: S_VALUES, c: i * 2 });
      if (s.money && ws[ref] && typeof ws[ref].v === 'number') ws[ref].z = MONEY;
    });
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, L.sheetName.slice(0, 31));
  return wb;
}

export function exportSheetExcel(input) {
  const L = sheetLayout(input);
  XLSX.writeFile(buildSheetWorkbook(L), `${L.fileBase}.xlsx`);
}

// ── PDF ─────────────────────────────────────────────────────────────────────

export function buildSheetPdf(input) {
  const L = sheetLayout(input);
  const doc = new jsPDF({ orientation: L.orientation, unit: 'mm', format: 'a4' });
  const PW = doc.internal.pageSize.getWidth();
  const PH = doc.internal.pageSize.getHeight();
  const M = 10;

  // Logoja është e KOMPANISË, jo e Nexuma-s: dokumentin e lëshon punëdhënësi.
  const logoW = drawCompanyLogo(doc, L.logo, M, 9.5, 10, 36);
  const titleX = M + (logoW ? logoW + 4 : 0);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...NAVY);
  doc.text(L.title, titleX, 16.5);

  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...INK);
  doc.text(L.left[0] || '—', M, 24);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...MUTED);
  L.left.slice(1).filter(Boolean).forEach((l, i) => doc.text(l, M, 28.5 + i * 3.6));
  L.right.filter(Boolean).forEach((l, i) => doc.text(l, PW - M, 24 + i * 3.6, { align: 'right' }));

  let ruleY = 38;
  if (L.note) {
    doc.setFont('helvetica', 'italic'); doc.setFontSize(6.6);
    doc.text(L.note, M, 41);
    ruleY = 43.5;
  }
  doc.setDrawColor(...BRAND); doc.setLineWidth(0.5);
  doc.line(M, ruleY, PW - M, ruleY);

  const cols = L.columns;
  const head = [];
  if (L.groups) {
    head.push(L.groups.map((g) => ({
      content: g.label || '',
      colSpan: g.keys.length,
      styles: { halign: 'center', fillColor: [255, 255, 255], textColor: MUTED, fontSize: 5.4, fontStyle: 'bold' },
    })));
  }
  head.push(cols.map((c) => ({
    content: c.label,
    styles: { halign: c.align || (c.money ? 'right' : 'left') },
  })));

  const foot = L.totals
    ? [cols.map((c, i) => {
        if (i === Math.min(1, cols.length - 1)) return { content: L.totalLabel, styles: { halign: 'left' } };
        // I njëjti rregull si te Excel-i: `money()` mbi një kohë do të jepte «0,00».
        const v = L.totals[c.key];
        if (v === undefined) return '';
        return Number.isFinite(Number(v)) && v !== '' ? money(v) : String(v);
      })]
    : undefined;

  // Ndarësit mes blloqeve — llogariten një herë, jo për çdo qelizë.
  const groupStarts = [];
  if (L.groups) { let acc = 0; L.groups.forEach((g) => { groupStarts.push(acc); acc += g.keys.length; }); }

  autoTable(doc, {
    head,
    body: L.body.map((b) => cols.map((c) => (c.money ? money(b[c.key]) : text(b[c.key])))),
    foot,
    startY: ruleY + 4,
    margin: { left: M, right: M, bottom: 18 },
    theme: 'plain',                       // rrjeta e plotë i mbyt numrat
    styles: { fontSize: 6.2, cellPadding: { top: 1.3, bottom: 1.3, left: 1.3, right: 1.3 }, textColor: INK, overflow: 'linebreak', valign: 'top' },
    headStyles: { fillColor: [248, 250, 252], textColor: INK, fontSize: 5.8, fontStyle: 'bold', lineColor: RULE, lineWidth: { bottom: 0.3 } },
    footStyles: { fillColor: [255, 255, 255], textColor: NAVY, fontSize: 6.4, fontStyle: 'bold', lineColor: BRAND, lineWidth: { top: 0.5 } },
    bodyStyles: { lineColor: [241, 245, 249], lineWidth: { bottom: 0.1 } },
    columnStyles: Object.fromEntries(cols.map((c, i) => [i, {
      halign: c.align || (c.money ? 'right' : 'left'),
      fontStyle: c.bold ? 'bold' : 'normal',
      textColor: c.muted ? MUTED : INK,
      cellWidth: c.pdfW || 'auto',
    }])),
    didParseCell: (d) => {
      if (groupStarts.includes(d.column.index) && d.column.index !== 0) {
        d.cell.styles.lineWidth = { ...(d.cell.styles.lineWidth || {}), left: 0.1 };
        d.cell.styles.lineColor = RULE;
      }
    },
  });

  let y = (doc.lastAutoTable?.finalY || 60) + 7;

  if (L.summary && L.summary.length) {
    if (y > PH - 30) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(6); doc.setTextColor(...MUTED);
    if (L.summaryTitle) { doc.text(L.summaryTitle.toUpperCase(), M, y); y += 3; }

    // E fundit veçohet: është shifra që dokumenti ekziston për ta thënë.
    const cards = L.summary.slice(0, -1);
    const highlight = L.summary[L.summary.length - 1];
    const bw = 36, gap = 2.5;
    cards.forEach((c, i) => {
      const x = M + i * (bw + gap);
      doc.setDrawColor(...RULE); doc.setLineWidth(0.1);
      doc.roundedRect(x, y, bw, 11, 1, 1, 'S');
      doc.setFont('helvetica', 'normal'); doc.setFontSize(5.2); doc.setTextColor(...MUTED);
      doc.text(String(c.label).toUpperCase(), x + 2.5, y + 3.8);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...INK);
      doc.text(c.money ? `${money(c.value)} €` : text(c.value), x + 2.5, y + 8.6);
    });

    const cx = M + cards.length * (bw + gap);
    doc.setFillColor(...NAVY);
    doc.roundedRect(cx, y, 48, 11, 1, 1, 'F');
    doc.setFont('helvetica', 'normal'); doc.setFontSize(5.2); doc.setTextColor(255, 255, 255);
    doc.text(String(highlight.label).toUpperCase(), cx + 3, y + 3.8);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5);
    doc.text(highlight.money ? `${money(highlight.value)} €` : text(highlight.value), cx + 3, y + 8.8);
    y += 11;
  }

  if (L.signature) {
    let sy = y + 14;
    if (sy > PH - 16) { doc.addPage(); sy = 30; }
    doc.setDrawColor(...RULE); doc.setLineWidth(0.2);
    doc.line(M, sy, M + 45, sy);
    doc.line(PW - M - 70, sy, PW - M, sy);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(...MUTED);
    doc.text(L.signature.left || '', M, sy + 3.5);
    doc.text(L.signature.right || '', PW - M, sy + 3.5, { align: 'right' });
  }

  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(5.6); doc.setTextColor(...MUTED);
    doc.text(L.footer, M, PH - 7);
    doc.text(`${p} / ${pages}`, PW - M, PH - 7, { align: 'right' });
  }
  return doc;
}

export function downloadSheetPdf(input) {
  const L = sheetLayout(input);
  buildSheetPdf(L).save(`${L.fileBase}.pdf`);
}

/**
 * Printimi ndan të njëjtin dokument me shkarkimin.
 * `autoPrint` shkruan një veprim BRENDA PDF-së, ndaj ndërtimi duhet të jetë një
 * i vetëm — një i dytë do ta hidhte poshtë pikërisht atë veprim.
 */
export function printSheet(input) {
  const doc = buildSheetPdf(sheetLayout(input));
  doc.autoPrint();
  const url = doc.output('bloburl');
  const win = window.open(url, '_blank');
  if (win) return win;

  // Bllokuesi i dritareve e ndalte printimin pa asnjë shenjë: butoni «dukej se
  // nuk punon». Dokumenti nuk ka nevojë për dritare — hapet edhe nga një kornizë
  // e fshehur, dhe `autoPrint` brenda tij e nxjerr vetë dialogun. Kjo rrugë
  // ndizet VETËM kur dritarja bllokohet.
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  frame.src = url;
  document.body.appendChild(frame);
  // Korniza hiqet vonë: disa shfletues lexojnë ende nga dokumenti kur dialogu
  // është i hapur, dhe heqja e parakohshme e lë faqen bosh.
  setTimeout(() => { try { document.body.removeChild(frame); } catch { /* u hoq */ } }, 60000);
  return null;
}
